import { format, parseISO } from 'date-fns';
import { ChangeEvent, ReactElement } from 'react';
import { renderToString } from 'react-dom/server';
import { z } from 'zod';
import { ApplicantType, Period } from '@brighte/validation';
import { AddressType, QuoteStatus } from '../graphql';
import { KnownErrors } from './constants';

export type FormatDollarOptions = {
  hideZeroCents: boolean;
  zeroValue: string;
};

const defaultOptions: FormatDollarOptions = { hideZeroCents: false, zeroValue: 'No' };

export function formatDollars(amountInDollars: number | string | undefined, options?: FormatDollarOptions) {
  const optionsToUse = { ...defaultOptions, ...options };
  const parsedAmount = typeof amountInDollars === 'string' ? parseFloat(amountInDollars) : amountInDollars;

  if (parsedAmount === undefined || parsedAmount === 0) {
    return optionsToUse.zeroValue;
  }

  const amountToFormat = optionsToUse.hideZeroCents ? Math.round(parsedAmount) : parsedAmount;
  const formattedAmount = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amountToFormat);

  return optionsToUse.hideZeroCents ? formattedAmount.replace('.00', '') : formattedAmount;
}

export function formatAddress(address: AddressType) {
  const { unitNumber, streetNumber, streetName, suburb, state, postcode } = address;
  return `${unitNumber ? unitNumber + '/' : ''}${streetNumber} ${streetName}, ${suburb} ${state} ${postcode}`;
}

export const formatDate = (date, dateFormat = 'dd/MM/yyyy') => (date ? format(parseISO(date), dateFormat) : '');

export const extractStringFromComponent = (component: ReactElement) => {
  const htmlString = renderToString(component);
  // Insert '; ' before each closing list-item tag so nested TextList sub-bullets
  // are delimited when the result is used as plain text (e.g. submission payload).
  const withListSeparators = htmlString.replace(/<\/li>/gi, '; </li>');
  const parser = new DOMParser();
  const parsedDocument = parser.parseFromString(withListSeparators, 'text/html');
  return (parsedDocument.body.textContent ?? '').replace(/;\s*$/, '').trim();
};

export const setCheckboxValue = (event: ChangeEvent<HTMLInputElement>, label: ReactElement | string, setValue) => {
  const isChecked = event.target.value;
  const name = event.target.name;
  if (!isChecked) {
    setValue(name, '');
  } else {
    if (typeof label === 'string') {
      setValue(name, label);
    } else {
      const value = extractStringFromComponent(label) || '';
      setValue(name, value);
    }
  }
};

type ErrorWithMessage = {
  message: string;
};

const isErrorWithMessage = (error: unknown): error is ErrorWithMessage =>
  typeof error === 'object' &&
  error !== null &&
  'message' in error &&
  typeof (error as Record<string, unknown>).message === 'string';

const toErrorWithMessage = (maybeError: unknown): ErrorWithMessage => {
  if (isErrorWithMessage(maybeError)) {
    return maybeError;
  }

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    // fallback in case there's an error stringifying the maybeError
    // like with circular references for example.
    return new Error(String(maybeError));
  }
};

export const getErrorMessage = (error: unknown) => toErrorWithMessage(error).message;
export const capitalise = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export const getFormFields = <T extends z.ZodTypeAny>(schema: T, discriminator: string): string[] => {
  // get zod object keys recursively
  // make sure schema is not null or undefined
  if (schema === null || schema === undefined) return [];
  // check if schema is nullable or optional
  if (schema instanceof z.ZodNullable || schema instanceof z.ZodOptional)
    return getFormFields(schema.unwrap(), discriminator);
  // check if schema is an array
  if (schema instanceof z.ZodArray) return getFormFields(schema.element, discriminator);
  // check if schema is an object
  if (schema instanceof z.ZodObject) {
    // get key/value pairs from schema
    const entries = Object.entries(schema.shape);
    // loop through key/value pairs
    return entries.flatMap(([key, value]) => {
      // get nested keys
      const nested =
        value instanceof z.ZodType ? getFormFields(value, discriminator).map(subKey => `${key}.${subKey}`) : [];
      // return nested keys
      return nested.length ? nested : key;
    });
  }
  // check if schema is an effects
  if (schema instanceof z.ZodEffects) {
    return getFormFields(schema._def.schema, discriminator);
  }

  if (schema instanceof z.ZodDiscriminatedUnion) {
    const entries = schema._def.optionsMap.entries();

    for (const [id, fields] of entries) {
      if (id === discriminator) {
        return getFormFields(fields, discriminator);
      }
    }

    // If no option matched the discriminator (e.g. nested union keyed on a different field),
    // collect the union of all fields from every option so the caller can read whichever are present.
    const allFields = new Set<string>();
    for (const [, fields] of schema._def.optionsMap.entries()) {
      getFormFields(fields, discriminator).forEach(field => allFields.add(field));
    }
    return [...allFields];
  }
  // check if schema is an intersection
  if (schema instanceof z.ZodIntersection) {
    const leftIntersection = schema._def.left;
    const rightIntersection = schema._def.right;
    const formFields: string[] = [];

    [leftIntersection, rightIntersection].forEach(intersection => {
      if (intersection instanceof z.ZodObject) {
        formFields.push(...getFormFields(intersection, discriminator));
      }
    });

    [leftIntersection, rightIntersection].forEach(intersection => {
      if (intersection instanceof z.ZodDiscriminatedUnion) {
        const entries = intersection._def.optionsMap.entries();

        if (!discriminator) throw new Error('Discriminator is required to extract form fields');

        for (const [id, fields] of entries) {
          if (id === discriminator) {
            formFields.push(...getFormFields(fields, discriminator));
          }
        }
      }
    });

    return formFields;
  }
  return [];
};

export const isFieldExists = (names: string[], field: string) => names.some(name => name === field);

export function isJointApplicant() {
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get('joint') === 'true';
}

export function getTrackingId() {
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get('trackingId') ?? '';
}

export function getApplicantTypeAndTrackingIdFromUrl() {
  const trackingId = getTrackingId();
  const isJoint = isJointApplicant();

  return {
    trackingId,
    applicantType: isJoint ? ApplicantType.Joint : ApplicantType.Primary,
  };
}

export function deSlug(slug?: string) {
  if (!slug) {
    return '';
  }

  const words = slug.split('-');
  return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export function pascalToCamelCase(value: string) {
  if (value) {
    return value.charAt(0).toLowerCase() + value.slice(1);
  }
  return value;
}

export function toSnakeCase(value: string) {
  return value.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

type KnownErrorMessage = {
  error?: {
    message?: string;
  };
  source?: string;
};

export const isDDRumKnownError = (event: KnownErrorMessage): boolean => {
  const { error } = event;

  if (error) {
    const isExisting = Object.values(KnownErrors).find(knownError => error?.message?.includes(knownError));

    if (isExisting) return false;
  }

  return true;
};

export const maskString = (value: string, revealedLength = 4, mask = '*') =>
  `${value}`.slice(value.length <= revealedLength ? value.length : -revealedLength).padStart(`${value}`.length, mask);

export const maskEmail = (email: string) => {
  const name = email.split('@')[0];
  const domain = email.split('@')[1];
  const maskedName = maskString(name);
  return `${maskedName}${domain ? `@${domain}` : ''}`;
};

export const isFloat = (value: number): boolean => {
  return Number(value) === value && !Number.isInteger(value);
};

export const getAdminUserId = () => {
  const COOKIE_ADMIN_NAME = 'brighte_admin';

  const adminUser = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${COOKIE_ADMIN_NAME}=`))
    ?.split('=')[1];

  return adminUser;
};

export const convertAmountToMonthlyPeriod = (amount: number, period: Period): number => {
  switch (period) {
    case Period.Weekly:
      return (amount * 52) / 12;
    case Period.Fortnightly:
      return (amount * 26) / 12;
    case Period.Annually:
      return amount / 12;
    default:
      return amount;
  }
};

export const accessibleQuote = (quoteStatus: QuoteStatus): boolean => {
  const accessibleStatuses = [QuoteStatus.SENT, QuoteStatus.STARTED];
  return accessibleStatuses.includes(quoteStatus);
};

export type ManualOccupationJobTitleMatchingType = {
  jobTitle: string;
  isFetched: boolean;
  isMatching: boolean;
  isMatched: boolean;
};
